import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Columns3,
  Copy,
  ExternalLink,
  EyeOff,
  FolderInput,
  Globe2,
  ListFilter,
  MapPin,
  MessageSquareWarning,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  X,
} from "lucide-react";
import {
  API,
  Button,
  Card,
  EmptyState,
  Modal,
  Pagination,
  SearchBar,
  Skeleton,
  apiFetch,
  cx,
  iCls,
  useToast,
} from "../../../pages/admin/_shared";
import { resolveAssetDisplayUrl } from "../../../lib/assetUrl";
import { usePermission } from "../../../hooks/usePermission";
import { STATUS_META, STATUS_ORDER, completenessOf, statusOf, transitionsFor } from "./workflow";

const SORTS = [
  { value: "createdAt:desc", label: "Mới tạo nhất" },
  { value: "updatedAt:desc", label: "Cập nhật gần đây" },
  { value: "name:asc", label: "Tên A → Z" },
  { value: "name:desc", label: "Tên Z → A" },
  { value: "viewCount:desc", label: "Nhiều lượt xem" },
  { value: "viewCount:asc", label: "Ít lượt xem" },
];
const SORT_VALUES = SORTS.map((s) => s.value);

/** Bộ lọc "Chất lượng nội dung" — khớp tham số `quality` ở backend. */
const QUALITY = [
  { value: "noImage", label: "Thiếu ảnh đại diện" },
  { value: "shortDesc", label: "Mô tả ngắn (< 80 ký tự)" },
  { value: "noSeo", label: "Thiếu SEO" },
  { value: "noGallery", label: "Thư viện < 3 ảnh" },
];

const PAGE_SIZES = [20, 50, 100];

/** Cột tuỳ chọn (ẩn mặc định để bảng gọn ở 1366px). */
const OPTIONAL_COLUMNS = [
  { key: "coords", label: "Toạ độ" },
  { key: "price", label: "Giá" },
  { key: "views", label: "Lượt xem" },
  { key: "hours", label: "Giờ mở cửa" },
];
const COLS_KEY = "adminLocationsColumns";

function readCols() {
  try {
    const v = JSON.parse(localStorage.getItem(COLS_KEY) || "[]");
    return new Set(Array.isArray(v) ? v : []);
  } catch {
    return new Set();
  }
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const fmtPrice = (loc) => {
  const min = Number(loc.priceMin ?? loc.price ?? 0);
  const max = Number(loc.priceMax ?? 0);
  if (!min && !max) return "—";
  if (min && max && min !== max) return `${min.toLocaleString("vi-VN")}₫ – ${max.toLocaleString("vi-VN")}₫`;
  return `${(min || max).toLocaleString("vi-VN")}₫`;
};

function Thumb({ loc }) {
  const src = typeof loc.image === "string" ? loc.image : resolveAssetDisplayUrl(loc.image?.url, loc.image?.key);
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-11 w-11 shrink-0 rounded-xl object-cover ring-1 ring-admin-border"
        onError={(e) => {
          e.currentTarget.style.visibility = "hidden";
        }}
      />
    );
  }
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-admin-muted ring-1 ring-admin-border" title="Chưa có ảnh đại diện">
      <MapPin size={17} className="text-admin-textMuted" />
    </span>
  );
}

/** Nhãn trạng thái (chỉ hiển thị — đổi trạng thái qua menu "…" để tránh bấm nhầm). */
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset", m.badge)} title={m.hint}>
      <span className={cx("h-1.5 w-1.5 rounded-full", m.dot)} />
      {m.label}
    </span>
  );
}

function Completeness({ loc }) {
  const c = completenessOf(loc);
  const tone = c.blockers.length ? "bg-amber-500" : c.percent === 100 ? "bg-emerald-500" : "bg-admin-primary";
  const tip = c.missing.length ? `Còn thiếu: ${c.missing.map((m) => m.label).join(", ")}` : "Đầy đủ";
  return (
    <div className="flex min-w-[96px] items-center gap-2" title={tip}>
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-admin-muted" role="progressbar" aria-valuenow={c.percent} aria-valuemin={0} aria-valuemax={100} aria-label="Độ hoàn thiện">
        <div className={cx("h-full rounded-full", tone)} style={{ width: `${c.percent}%` }} />
      </div>
      <span className="text-xs font-semibold tabular-nums text-admin-text">{c.percent}%</span>
    </div>
  );
}

/** Menu "…" của dòng: nhóm thao tác xem / sửa / chuyển trạng thái / xoá, tách riêng nút xoá. */
function RowMenu({ loc, onEdit, onAction, can }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const status = statusOf(loc);
  const transitions = transitionsFor(status, can);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const Item = ({ icon: Icon, label, hint, onClick, danger, disabled, href }) => {
    const cls = cx(
      "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition",
      disabled ? "cursor-not-allowed opacity-50" : danger ? "hover:bg-rose-50" : "hover:bg-admin-muted"
    );
    const inner = (
      <>
        <Icon size={15} className={cx("mt-0.5 shrink-0", danger ? "text-rose-600" : "text-admin-textMuted")} />
        <span className="min-w-0">
          <span className={cx("block text-sm font-medium", danger ? "text-rose-600" : "text-admin-text")}>{label}</span>
          {hint ? <span className="block text-[11px] text-admin-textMuted">{hint}</span> : null}
        </span>
      </>
    );
    return href && !disabled ? (
      <a href={href} target="_blank" rel="noopener noreferrer" role="menuitem" className={cls} onClick={() => setOpen(false)}>
        {inner}
      </a>
    ) : (
      <button
        type="button"
        role="menuitem"
        disabled={disabled}
        className={cls}
        onClick={() => {
          setOpen(false);
          onClick?.();
        }}
      >
        {inner}
      </button>
    );
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Thao tác với ${loc.name}`}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
      >
        <MoreHorizontal size={17} />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-64 rounded-xl border border-admin-border bg-admin-surface p-1.5 shadow-soft-xl" style={{ animation: "admin-pop-in 0.12s ease-out" }}>
          <p className="px-2.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-admin-textMuted">Xem</p>
          <Item
            icon={ExternalLink}
            label="Xem trên website"
            hint={status === "published" ? "Mở trang công khai ở tab mới" : "Chưa đăng nên chưa có trang công khai"}
            disabled={status !== "published"}
            href={`/location/${loc.slug}`}
          />

          {can("locations:write") ? (
            <>
              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-admin-textMuted">Sửa</p>
              <Item icon={Pencil} label="Chỉnh sửa" onClick={() => onEdit(loc)} />
              <Item icon={Copy} label="Sao chép" hint="Tạo bản nháp mới từ địa điểm này" onClick={() => onAction("duplicate", loc)} />
            </>
          ) : null}

          {transitions.length ? (
            <>
              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-admin-textMuted">Trạng thái</p>
              {transitions.map((t) => (
                <Item
                  key={t.to + t.label}
                  icon={t.to === "published" ? Globe2 : t.to === "review" ? Send : EyeOff}
                  label={t.label}
                  hint={t.hint}
                  danger={t.tone === "danger"}
                  onClick={() => onAction("status", loc, t)}
                />
              ))}
            </>
          ) : null}

          {can("locations:delete") ? (
            <>
              <div className="my-1.5 h-px bg-admin-border" />
              <Item icon={Trash2} label="Xoá địa điểm" hint="Xoá vĩnh viễn, không hoàn tác được" danger onClick={() => onAction("delete", loc)} />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Hộp chọn nhiều giá trị (Chất lượng nội dung). */
function MultiFilter({ label, icon: Icon, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          "inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition",
          value.length ? "border-admin-primary bg-admin-primaryLight text-admin-primary" : "border-admin-border bg-admin-surface text-admin-text hover:bg-admin-muted"
        )}
      >
        <Icon size={15} />
        {label}
        {value.length ? <span className="rounded-full bg-admin-primary px-1.5 text-[11px] font-semibold leading-4 text-white">{value.length}</span> : null}
      </button>
      {open ? (
        <ul role="listbox" aria-multiselectable className="absolute left-0 top-full z-30 mt-1 w-64 rounded-xl border border-admin-border bg-admin-surface p-1.5 shadow-soft-xl" style={{ animation: "admin-pop-in 0.12s ease-out" }}>
          {options.map((o) => (
            <li key={o.value} role="option" aria-selected={value.includes(o.value)}>
              <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-admin-text hover:bg-admin-muted">
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} className="h-4 w-4 rounded accent-[rgb(var(--admin-primary))]" />
                {o.label}
              </label>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Chip bộ lọc đang áp dụng, bấm × để bỏ. */
const Chip = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-admin-primaryLight py-1 pl-3 pr-1.5 text-xs font-medium text-admin-primary">
    {children}
    <button type="button" onClick={onRemove} aria-label="Bỏ bộ lọc này" className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-admin-primary/15">
      <X size={11} />
    </button>
  </span>
);

const qs = (params, key, fallback = "") => params.get(key) || fallback;

/**
 * Danh sách địa điểm: lọc (tìm kiếm · danh mục · trạng thái · chất lượng) giữ trên URL, sắp xếp, phân trang phía server,
 * thao tác hàng loạt, menu "…" cho từng dòng, xác nhận nêu rõ tác động và Hoàn tác khi đổi trạng thái.
 */
export default function LocationList({ categories = [], reloadKey = 0, onAdd, onEdit, addDisabled = false }) {
  const { push: toast, ToastPortal } = useToast();
  const { can } = usePermission();
  const [params, setParams] = useSearchParams();

  // ── Bộ lọc: nguồn sự thật là URL (chia sẻ/quay lại được) ──
  const status = STATUS_ORDER.includes(qs(params, "status")) ? qs(params, "status") : "";
  const category = qs(params, "category");
  const search = qs(params, "q");
  const quality = qs(params, "quality").split(",").filter((x) => QUALITY.some((q) => q.value === x));
  const sort = SORT_VALUES.includes(qs(params, "sort")) ? qs(params, "sort") : "createdAt:desc";
  const page = Math.max(1, parseInt(qs(params, "page", "1"), 10) || 1);
  const limit = PAGE_SIZES.includes(parseInt(qs(params, "limit"), 10)) ? parseInt(qs(params, "limit"), 10) : 20;

  const patch = useCallback(
    (changes, { resetPage = true } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([k, v]) => {
            const empty = v == null || v === "" || (Array.isArray(v) && !v.length);
            if (empty) next.delete(k);
            else next.set(k, Array.isArray(v) ? v.join(",") : String(v));
          });
          if (resetPage) next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    if (searchInput === search) return undefined;
    const t = setTimeout(() => patch({ q: searchInput.trim() }), 300);
    return () => clearTimeout(t);
  }, [searchInput, search, patch]);

  const [state, setState] = useState({ rows: [], total: 0, pages: 1, loading: true, error: "" });
  const [stats, setStats] = useState({ total: 0, byStatus: { draft: 0, review: 0, published: 0 } });
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: "delete"|"unpublish", locs: [] }
  const [noteDialog, setNoteDialog] = useState(null);
  const [blockDialog, setBlockDialog] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkCategory, setBulkCategory] = useState("");
  const [version, setVersion] = useState(0);
  const [cols, setCols] = useState(readCols);
  const [colsOpen, setColsOpen] = useState(false);
  const colsRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!colsOpen) return undefined;
    const onDown = (e) => colsRef.current && !colsRef.current.contains(e.target) && setColsOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [colsOpen]);
  const toggleCol = (k) =>
    setCols((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      try {
        localStorage.setItem(COLS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });

  // Đổi bộ lọc → bỏ chọn
  const filterKey = `${status}|${category}|${search}|${quality.join(",")}|${sort}|${limit}|${page}`;
  useEffect(() => setSelected(new Set()), [filterKey]);

  const load = useCallback(async () => {
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const [sortField, sortOrder] = sort.split(":");
    const q = new URLSearchParams({ page: String(page), limit: String(limit), sort: sortField, order: sortOrder });
    if (status) q.set("status", status);
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    if (quality.length) q.set("quality", quality.join(","));
    try {
      const [res, st] = await Promise.all([apiFetch(`${API}/locations?${q.toString()}`), apiFetch(`${API}/locations/admin/stats`).catch(() => null)]);
      if (id !== seq.current) return;
      setState({ rows: res.data || [], total: res.total || 0, pages: res.pages || 1, loading: false, error: "" });
      if (st) setStats(st);
      // Đang ở trang không còn dữ liệu (vừa xoá dòng cuối, hoặc ?page quá lớn) → lùi về trang cuối thay vì hiện "trống"
      if (page > 1 && (res.total || 0) > 0 && !(res.data || []).length) {
        const last = Math.max(1, res.pages || 1);
        patch({ page: last === 1 ? "" : last }, { resetPage: false });
      }
    } catch (e) {
      if (id === seq.current) setState((s) => ({ ...s, loading: false, error: e?.message || "Không tải được danh sách" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    load();
  }, [load, reloadKey, version]);

  const refresh = () => setVersion((v) => v + 1);

  const rows = state.rows;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r._id));
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r._id)));
  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── Đổi trạng thái 1 địa điểm (kèm Hoàn tác) ──
  const runStatus = async (loc, to, { note = "", force = false, undo = true } = {}) => {
    const from = statusOf(loc);
    setBusy(true);
    try {
      await apiFetch(`${API}/locations/${loc._id}/status`, { method: "PATCH", body: JSON.stringify({ status: to, note, force }) });
      toast(`"${loc.name}": ${STATUS_META[from].label} → ${STATUS_META[to].label}`, "success", {
        action: undo ? { label: "Hoàn tác", onClick: () => runStatus({ ...loc, status: to }, from, { undo: false, note: "Hoàn tác" }) } : undefined,
      });
      refresh();
    } catch (e) {
      if (e.code === "PUBLISH_REQUIREMENTS") setBlockDialog({ loc, to, blockers: e.details?.blockers || [], canForce: Boolean(e.details?.canForce) });
      else toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (loc) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/locations/${loc._id}/duplicate`, { method: "POST" });
      toast(`Đã tạo bản nháp "${res.data?.name}"`, "success", { action: { label: "Chỉnh sửa ngay", onClick: () => onEdit(res.data) } });
      refresh();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const onAction = (kind, loc, option) => {
    if (kind === "duplicate") return duplicate(loc);
    if (kind === "delete") return setConfirm({ kind: "delete", locs: [loc] });
    if (kind === "status") {
      if (option.needNote) return setNoteDialog({ loc, to: option.to, note: "" });
      if (statusOf(loc) === "published" && option.to !== "published") return setConfirm({ kind: "unpublish", locs: [loc], to: option.to });
      return runStatus(loc, option.to);
    }
    return undefined;
  };

  // ── Hàng loạt ──
  const runBulk = async (body) => {
    setBusy(true);
    try {
      const res = await apiFetch(`${API}/locations/bulk`, { method: "POST", body: JSON.stringify({ ids: [...selected], ...body }) });
      toast(res.message, res.failed?.length ? "info" : "success");
      if (res.failed?.length) setBulkResult(res);
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const doConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === "unpublish") return runStatus(c.locs[0], c.to);
    if (c.locs.length === 1 && !c.bulk) {
      setBusy(true);
      try {
        await apiFetch(`${API}/locations/${c.locs[0]._id}`, { method: "DELETE" });
        toast(`Đã xoá "${c.locs[0].name}"`);
        refresh();
      } catch (e) {
        toast(e.message, "error");
      } finally {
        setBusy(false);
      }
      return undefined;
    }
    return runBulk({ action: "delete" });
  };

  const counts = stats.byStatus || {};
  const tabs = [{ value: "", label: "Tất cả", n: stats.total }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label, n: counts[s] || 0 }))];
  const catName = categories.find((c) => c._id === category)?.name || "Danh mục";
  const hasFilter = Boolean(status || category || search || quality.length);
  const selectedRows = rows.filter((r) => selected.has(r._id));
  const selStatuses = new Set(selectedRows.map(statusOf));

  return (
    <div className="space-y-5">
      {/* Tiêu đề */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-admin-text">Quản lý địa điểm</h1>
          <p className="mt-1 text-sm text-admin-textMuted">
            {stats.total} địa điểm · {counts.published || 0} đã đăng
            {counts.review ? <span className="font-semibold text-amber-700"> · {counts.review} chờ duyệt</span> : null}
            {counts.draft ? ` · ${counts.draft} nháp` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={RefreshCw} onClick={refresh} loading={state.loading && rows.length > 0}>
            Làm mới
          </Button>
          {can("locations:write") ? (
            <Button variant="primary" icon={Plus} onClick={onAdd} disabled={addDisabled} title={addDisabled ? "Đang có tiến trình thêm địa điểm" : undefined}>
              Thêm địa điểm
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        {/* Tab trạng thái */}
        <div role="tablist" aria-label="Lọc theo trạng thái" className="flex flex-wrap items-center gap-1 border-b border-admin-border px-3 pt-2">
          {tabs.map((t) => {
            const on = status === t.value;
            return (
              <button
                key={t.value || "all"}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => patch({ status: t.value })}
                className={cx("-mb-px inline-flex items-center gap-2 border-b-2 px-3 pb-2.5 pt-1.5 text-sm font-medium transition", on ? "border-admin-primary text-admin-text" : "border-transparent text-admin-textMuted hover:text-admin-text")}
              >
                {t.value ? <span className={cx("h-2 w-2 rounded-full", STATUS_META[t.value].dot)} /> : null}
                {t.label}
                <span className={cx("rounded-full px-1.5 text-[11px] font-semibold tabular-nums", on ? "bg-admin-primaryLight text-admin-primary" : "bg-admin-muted text-admin-textMuted")}>{t.n}</span>
              </button>
            );
          })}
        </div>

        {/* Thanh công cụ: [Tìm kiếm] [Danh mục] [Chất lượng] [Sắp xếp] [Cột] */}
        <div className="flex flex-wrap items-center gap-3 p-3">
          <SearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Tìm tên, địa chỉ, slug…" className="w-full sm:w-64" />
          <select value={category} onChange={(e) => patch({ category: e.target.value })} aria-label="Lọc theo danh mục" className={cx(iCls, "!h-10 !w-auto min-w-[150px] !py-0")}>
            <option value="">Tất cả danh mục</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <MultiFilter label="Chất lượng nội dung" icon={ListFilter} options={QUALITY} value={quality} onChange={(v) => patch({ quality: v })} />
          <select value={sort} onChange={(e) => patch({ sort: e.target.value === "createdAt:desc" ? "" : e.target.value }, { resetPage: false })} aria-label="Sắp xếp" className={cx(iCls, "!h-10 !w-auto min-w-[150px] !py-0")}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <div className="relative ml-auto" ref={colsRef}>
            <button type="button" onClick={() => setColsOpen((o) => !o)} aria-haspopup="menu" aria-expanded={colsOpen} className="inline-flex h-10 items-center gap-2 rounded-lg border border-admin-border bg-admin-surface px-3 text-sm font-medium text-admin-text transition hover:bg-admin-muted">
              <Columns3 size={15} /> Cột
            </button>
            {colsOpen ? (
              <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-52 rounded-xl border border-admin-border bg-admin-surface p-1.5 shadow-soft-xl" style={{ animation: "admin-pop-in 0.12s ease-out" }}>
                <p className="px-2.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-admin-textMuted">Hiện thêm cột</p>
                {OPTIONAL_COLUMNS.map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-admin-text hover:bg-admin-muted">
                    <input type="checkbox" checked={cols.has(c.key)} onChange={() => toggleCol(c.key)} className="h-4 w-4 rounded accent-[rgb(var(--admin-primary))]" />
                    {c.label}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Chip bộ lọc đang áp dụng + số kết quả chính xác */}
        {hasFilter ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-admin-border px-3 py-2.5" aria-live="polite">
            <span className="text-xs text-admin-textMuted">
              <span className="font-semibold text-admin-text">{state.loading ? "…" : state.total}</span> kết quả
            </span>
            {status ? <Chip onRemove={() => patch({ status: "" })}>Trạng thái: {STATUS_META[status].label}</Chip> : null}
            {category ? <Chip onRemove={() => patch({ category: "" })}>{catName}</Chip> : null}
            {search ? <Chip onRemove={() => patch({ q: "" })}>“{search}”</Chip> : null}
            {quality.map((q) => (
              <Chip key={q} onRemove={() => patch({ quality: quality.filter((x) => x !== q) })}>{QUALITY.find((o) => o.value === q)?.label}</Chip>
            ))}
            <button type="button" onClick={() => setParams({}, { replace: true })} className="ml-1 text-xs font-semibold text-admin-primary hover:underline">
              Xoá lọc
            </button>
          </div>
        ) : null}

        {/* Thao tác hàng loạt */}
        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-y border-admin-border bg-admin-primaryLight/60 px-4 py-2.5" role="region" aria-label="Thao tác hàng loạt">
            <p className="mr-1 text-sm text-admin-text">Đã chọn <span className="font-semibold">{selected.size}</span></p>
            {can("locations:write") && !selStatuses.has("review") && !selStatuses.has("published") ? (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => runBulk({ action: "status", status: "review" })}>Gửi duyệt</Button>
            ) : null}
            {can("locations:publish") ? (
              <>
                <Button size="sm" variant="primary" disabled={busy} onClick={() => runBulk({ action: "status", status: "published" })}>Đăng</Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    // Trả bài đang chờ duyệt về nháp bắt buộc có lý do để người viết biết cần sửa gì
                    let note;
                    if (selStatuses.has("review")) {
                      note = window.prompt("Lý do trả về nháp (bắt buộc, sẽ hiển thị cho người viết):", "");
                      if (!note || !note.trim()) return;
                    }
                    runBulk({ action: "status", status: "draft", ...(note ? { note: note.trim() } : {}) });
                  }}
                >
                  Chuyển về nháp
                </Button>
              </>
            ) : null}
            {can("locations:write") ? (
              <span className="flex items-center gap-1.5">
                <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} aria-label="Danh mục đích" className={cx(iCls, "!h-8 !w-auto !py-0 !text-xs")}>
                  <option value="">Đổi danh mục…</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
                <Button size="sm" variant="secondary" icon={FolderInput} disabled={!bulkCategory || busy} onClick={() => { runBulk({ action: "category", category: bulkCategory }); setBulkCategory(""); }}>
                  Áp dụng
                </Button>
              </span>
            ) : null}
            {can("locations:delete") ? (
              <Button size="sm" variant="danger" icon={Trash2} disabled={busy} onClick={() => setConfirm({ kind: "delete", locs: selectedRows, bulk: true })}>Xoá</Button>
            ) : null}
            <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-xs font-semibold text-admin-textMuted hover:text-admin-text">Bỏ chọn</button>
          </div>
        ) : null}

        {/* Bảng */}
        {state.loading && rows.length === 0 ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-11 w-11 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-1/3" />
                  <Skeleton className="h-3 w-1/5" />
                </div>
              </div>
            ))}
          </div>
        ) : state.error ? (
          <EmptyState icon={AlertTriangle} title="Không tải được danh sách" description={state.error} action={<Button variant="primary" onClick={refresh}>Thử lại</Button>} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={MapPin}
            title={hasFilter ? "Không có địa điểm phù hợp" : "Chưa có địa điểm nào"}
            description={hasFilter ? "Thử bỏ bớt bộ lọc." : "Thêm địa điểm đầu tiên cho bản đồ du lịch."}
            action={hasFilter ? <Button variant="secondary" onClick={() => setParams({}, { replace: true })}>Xoá lọc</Button> : can("locations:write") ? <Button variant="primary" icon={Plus} onClick={onAdd}>Thêm địa điểm</Button> : null}
          />
        ) : (
          <div className={cx("overflow-x-auto transition-opacity", state.loading && "opacity-60")}>
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-admin-muted/60 text-[11px] uppercase tracking-wider text-admin-textMuted">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input type="checkbox" checked={allOnPage} onChange={toggleAll} aria-label="Chọn tất cả trên trang" className="h-4 w-4 cursor-pointer rounded accent-[rgb(var(--admin-primary))]" />
                  </th>
                  <th className="px-3 py-3 text-left font-semibold">Địa điểm</th>
                  <th className="px-3 py-3 text-left font-semibold">Danh mục</th>
                  <th className="px-3 py-3 text-left font-semibold">Trạng thái</th>
                  <th className="px-3 py-3 text-left font-semibold">Hoàn thiện</th>
                  {cols.has("coords") ? <th className="px-3 py-3 text-left font-semibold">Toạ độ</th> : null}
                  {cols.has("price") ? <th className="px-3 py-3 text-left font-semibold">Giá</th> : null}
                  {cols.has("hours") ? <th className="px-3 py-3 text-left font-semibold">Giờ mở cửa</th> : null}
                  {cols.has("views") ? <th className="px-3 py-3 text-right font-semibold">Lượt xem</th> : null}
                  <th className="px-3 py-3 text-left font-semibold">Cập nhật</th>
                  <th className="w-12 px-3 py-3"><span className="sr-only">Thao tác</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-border">
                {rows.map((loc) => {
                  const st = statusOf(loc);
                  const on = selected.has(loc._id);
                  return (
                    <tr key={loc._id} className={cx("transition-colors", on ? "bg-admin-primaryLight/50" : "hover:bg-admin-muted/40")}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={on} onChange={() => toggleOne(loc._id)} aria-label={`Chọn ${loc.name}`} className="h-4 w-4 cursor-pointer rounded accent-[rgb(var(--admin-primary))]" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <Thumb loc={loc} />
                          <div className="min-w-0 max-w-[280px]">
                            <p className="truncate font-semibold text-admin-text">{loc.name}</p>
                            <p className="truncate font-mono text-[11px] text-admin-textMuted">/{loc.slug || "—"}</p>
                            {st === "draft" && loc.reviewNote ? (
                              <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] font-medium text-amber-700" title={loc.reviewNote}>
                                <MessageSquareWarning size={11} className="shrink-0" /> Bị trả về: {loc.reviewNote}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-block max-w-[150px] truncate rounded-md bg-admin-muted px-2 py-0.5 text-xs font-medium text-admin-text">{loc.category?.name || loc.categoryName || "—"}</span>
                      </td>
                      <td className="px-3 py-3"><StatusBadge status={st} /></td>
                      <td className="px-3 py-3"><Completeness loc={loc} /></td>
                      {cols.has("coords") ? <td className="px-3 py-3 font-mono text-[11px] text-admin-textMuted">{Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}</td> : null}
                      {cols.has("price") ? <td className="px-3 py-3 text-xs tabular-nums text-admin-text">{fmtPrice(loc)}</td> : null}
                      {cols.has("hours") ? <td className="px-3 py-3 text-xs text-admin-text">{loc.openTime || loc.closeTime ? `${loc.openTime || "—"} – ${loc.closeTime || "—"}` : "—"}</td> : null}
                      {cols.has("views") ? <td className="px-3 py-3 text-right tabular-nums text-admin-text">{Number(loc.viewCount || 0).toLocaleString("vi-VN")}</td> : null}
                      <td className="px-3 py-3 text-xs text-admin-textMuted">{fmtDate(loc.updatedAt)}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {can("locations:write") ? (
                            <button type="button" onClick={() => onEdit(loc)} aria-label={`Chỉnh sửa ${loc.name}`} title="Chỉnh sửa" className="hidden h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text lg:flex">
                              <Pencil size={14} />
                            </button>
                          ) : null}
                          <RowMenu loc={loc} onEdit={onEdit} onAction={onAction} can={can} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Phân trang */}
        {rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-admin-border">
            <Pagination page={page} pages={state.pages} total={state.total} onChange={(p) => patch({ page: p === 1 ? "" : p }, { resetPage: false })} />
            <label className="flex items-center gap-2 px-5 text-xs text-admin-textMuted">
              Hiện
              <select value={limit} onChange={(e) => patch({ limit: e.target.value === "20" ? "" : e.target.value })} className={cx(iCls, "!h-8 !w-auto !py-0 !text-xs")} aria-label="Số dòng mỗi trang">
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              dòng
            </label>
          </div>
        ) : null}
      </Card>

      {/* Xác nhận có tên bản ghi + nêu rõ tác động */}
      {confirm ? (
        <Modal
          open
          onClose={() => setConfirm(null)}
          maxWidthClass="max-w-md"
          title={
            <span className="flex items-center gap-2 text-base font-semibold text-admin-text">
              {confirm.kind === "delete" ? <Trash2 size={18} className="text-rose-600" /> : <EyeOff size={18} className="text-amber-600" />}
              {confirm.kind === "delete"
                ? confirm.locs.length === 1 ? `Xoá “${confirm.locs[0].name}”?` : `Xoá ${confirm.locs.length} địa điểm?`
                : `Gỡ “${confirm.locs[0].name}” khỏi website?`}
            </span>
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>Huỷ</Button>
              <Button variant={confirm.kind === "delete" ? "danger" : "primary"} onClick={doConfirm} autoFocus>
                {confirm.kind === "delete" ? "Xoá vĩnh viễn" : "Gỡ xuống"}
              </Button>
            </div>
          }
        >
          {confirm.kind === "delete" ? (
            <div className="space-y-3 text-sm text-admin-text">
              <ul className="max-h-32 list-inside list-disc overflow-y-auto rounded-lg bg-admin-muted px-3 py-2 text-xs">
                {confirm.locs.slice(0, 8).map((l) => (
                  <li key={l._id}>{l.name} <span className="text-admin-textMuted">— {STATUS_META[statusOf(l)].label}, {Number(l.viewCount || 0).toLocaleString("vi-VN")} lượt xem</span></li>
                ))}
                {confirm.locs.length > 8 ? <li>… và {confirm.locs.length - 8} địa điểm khác</li> : null}
              </ul>
              <p>Sẽ <b>biến mất khỏi website và bản đồ</b>, mất lượt xem và nội dung đã nhập. Ảnh đã tải lên vẫn nằm trong thư viện media.</p>
              <p className="rounded-lg bg-rose-50 px-3 py-2 text-rose-700">Không thể hoàn tác. Nếu chỉ muốn tạm ẩn, hãy chọn “Gỡ xuống (về nháp)”.</p>
            </div>
          ) : (
            <div className="space-y-2 text-sm text-admin-text">
              <p>Địa điểm sẽ <b>ngừng hiển thị ngay</b> trên website và bản đồ, rồi chuyển sang trạng thái <b>Nháp</b>.</p>
              <p className="text-admin-textMuted">Nội dung được giữ nguyên. Bạn có thể bấm “Hoàn tác” ngay sau đó hoặc đăng lại bất cứ lúc nào.</p>
            </div>
          )}
        </Modal>
      ) : null}

      {/* Trả về nháp: bắt buộc ghi lý do */}
      {noteDialog ? (
        <Modal
          open
          onClose={() => setNoteDialog(null)}
          title="Trả địa điểm về nháp"
          maxWidthClass="max-w-md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setNoteDialog(null)}>Huỷ</Button>
              <Button variant="primary" disabled={!noteDialog.note.trim() || busy} onClick={() => { const d = noteDialog; setNoteDialog(null); runStatus(d.loc, d.to, { note: d.note.trim() }); }}>
                Trả về nháp
              </Button>
            </div>
          }
        >
          <p className="text-sm text-admin-text">Ghi rõ cần sửa gì cho người soạn: <span className="font-semibold">{noteDialog.loc.name}</span></p>
          <textarea autoFocus rows={4} maxLength={500} value={noteDialog.note} onChange={(e) => setNoteDialog((d) => ({ ...d, note: e.target.value }))} placeholder="Ví dụ: Mô tả còn ngắn, cần thêm ảnh bìa…" className={cx(iCls, "mt-3 resize-none")} />
        </Modal>
      ) : null}

      {/* Chưa đủ điều kiện đăng */}
      {blockDialog ? (
        <Modal
          open
          onClose={() => setBlockDialog(null)}
          title={<span className="flex items-center gap-2 text-base font-semibold text-admin-text"><AlertTriangle size={18} className="text-amber-600" /> Chưa đủ điều kiện để đăng</span>}
          maxWidthClass="max-w-md"
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setBlockDialog(null)}>Đóng</Button>
              {blockDialog.canForce ? <Button variant="danger" onClick={() => { const d = blockDialog; setBlockDialog(null); runStatus(d.loc, d.to, { force: true }); }}>Vẫn đăng</Button> : null}
              <Button variant="primary" onClick={() => { const l = blockDialog.loc; setBlockDialog(null); onEdit(l); }}>Chỉnh sửa địa điểm</Button>
            </div>
          }
        >
          <p className="text-sm text-admin-text"><span className="font-semibold">{blockDialog.loc.name}</span> còn thiếu:</p>
          <ul className="mt-3 space-y-2">
            {blockDialog.blockers.map((b) => (
              <li key={b.field} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"><AlertTriangle size={15} className="mt-0.5 shrink-0" /> {b.message}</li>
            ))}
          </ul>
        </Modal>
      ) : null}

      {/* Kết quả hàng loạt có mục thất bại */}
      {bulkResult ? (
        <Modal
          open
          onClose={() => setBulkResult(null)}
          title={`Đã xử lý ${bulkResult.ok}, ${bulkResult.failed.length} không thực hiện được`}
          maxWidthClass="max-w-lg"
          footer={<div className="flex justify-end"><Button variant="primary" onClick={() => setBulkResult(null)}>Đã hiểu</Button></div>}
        >
          <ul className="max-h-[320px] divide-y divide-admin-border overflow-y-auto rounded-lg border border-admin-border">
            {bulkResult.failed.map((f) => (
              <li key={f.id} className="px-3 py-2.5">
                <p className="text-sm font-semibold text-admin-text">{f.name || f.id}</p>
                <p className="text-xs text-rose-700">{f.reason}</p>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}

      <ToastPortal />
    </div>
  );
}

